/**
 * Balises de VENTILATION CUISINE — `[CAT:<id>|<nom>:START] … [CAT:<id>|<nom>:END]`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ═══════════════════════════════════════════════════════════════════════════════
 * Ces balises sont le SEUL mécanisme de routage vers les imprimantes cuisine. Côté
 * borne c'est `utils/ticketSplitter.ts:34` qui les consomme :
 *
 *     /\[CAT:([^|]+)\|([^:]+):START\]([\s\S]*?)\[CAT:\1\|\2:END\]/g
 *
 * Sans balise → aucun bloc trouvé → aucun ticket cuisine (silence total, sans erreur).
 *
 * ⚠️ La regex de la borne est PLUS STRICTE que celle de la console : elle exige que le
 * NOM soit identique entre START et END (`\2`) et qu'il ne contienne AUCUN `:`
 * (`[^:]+`). Un nom de catégorie du genre « Menu : Midi » ou « Boissons [Bar] » ferait
 * donc échouer la découpe ET `stripMarkers()` — la balise brute finirait IMPRIMÉE sur
 * le ticket client. D'où l'échappement centralisé ci-dessous.
 *
 * ⚠️ L'échappement s'applique UNIQUEMENT à la balise (consommée par le JS), jamais au
 * titre affiché sur le ticket : le client doit voir le vrai nom de la catégorie.
 */

/** Caractères qui cassent la regex de `ticketSplitter` / `stripMarkers`. */
const UNSAFE = /[[\]|:]/g;

/**
 * Nom de catégorie utilisable DANS la balise.
 * Le repli `Categorie` (jamais affiché) évite un groupe vide qui ferait échouer `([^:]+)`.
 */
export const safeCategoryName = (name: unknown): string =>
  String(name ?? '')
    .replace(UNSAFE, '_')
    .replace(/\s+/g, ' ')
    .trim() || 'Categorie';

/**
 * Identifiant de catégorie utilisable DANS la balise.
 * ⚠️ Il doit rester IDENTIQUE à celui enregistré dans le mapping de routage
 * (`printer-routing-db` → `categoryMappings[].categoryId`), sinon plus rien ne
 * correspond : on se contente de neutraliser les caractères interdits (absents des
 * identifiants réels) et de fournir un repli quand l'id est vide.
 */
export const safeCategoryId = (id: unknown, fallbackIndex = 0): string =>
  String(id ?? '').replace(UNSAFE, '_').trim() || `cat-${fallbackIndex}`;

/** Balise d'OUVERTURE. À pousser SEULE sur sa ligne, sans préfixe d'alignement. */
export const catStart = (id: unknown, name: unknown, index = 0): string =>
  `[CAT:${safeCategoryId(id, index)}|${safeCategoryName(name)}:START]`;

/** Balise de FERMETURE. Id ET nom stricto identiques au START (`\1` et `\2`). */
export const catEnd = (id: unknown, name: unknown, index = 0): string =>
  `[CAT:${safeCategoryId(id, index)}|${safeCategoryName(name)}:END]`;
